<template>
	<div class="suggestion">

		<ovp-typography
			type="heading04"
			v-text="$t('product.suggestion.title', { savings })"
		/>

		<ovp-typography
			type="body04"
			v-text="$t('product.suggestion.subtitle')"
		/>

		<ovp-message>
			<div v-text="doctorName"></div>
		</ovp-message>

		<div class="suggestion-products">
			<template
				v-if="ready"
				v-for="sProduct in sortedProducts">
				<div
					class="suggestion-product"
					:key="sProduct.id">
					<ovp-replace-product
						:group-id="groupId"
						:person-id="personId"
						:product="sProduct"
						:product-id="sProduct.id"
						:selected="true"
						@replace="addProduct(personId, sProduct, doctor)"
					/>
				</div>
			</template>
			<ovp-loading v-else />
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { cloneDeep } from 'lodash'

import OvpReplaceProduct from '@/components/Product/Replace/Product'
import OvpLoading from '@/components/Container/LoadingAlt'
import OvpMessage from '@/components/Container/Message'

import BasketMixin from '@/mixins/Basket'

import { DoctorHelper, LogHelper, ProductHelper, } from '@/helpers'

import { EVENT_PRODUCT_ADDED } from '@/config/events'

export default {
	name: 'OvpReplace',

  mixins: [BasketMixin],

	components: {
		OvpLoading,
		OvpMessage,
		OvpReplaceProduct,
	},

	props: {
		categoryId: {
			type: String,
			required: true,
		},

		doctor: {
			type: Object,
			required: true,
		},

		groupId: {
			type: String,
			required: true,
		},

		personId: {
			type: String,
			required: true
		},

		product: {
			type: Object,
			required: true,
		},

		source: {
			type: String,
			required: true
		}
	},

	data () {
		return {
			products: [],
			ready: false,
		}
	},

	mounted () {
		this.simulate()
	},

	computed: {
    ...mapGetters('basket', ['getProductsFromGroup']),
		...mapState(['basket']),

		doctorName() {
      return DoctorHelper.getDoctorName(this.doctor)
    },

    savings () {
    	if (this.sortedProducts.length === 2) {
    		const savings = this.sortedProducts[1].prices.find(p => p.selected).price - this.sortedProducts[0].prices.find(p => p.selected).price
    		return parseFloat(savings).toFixed(2)
    	} else {
    		return 0.00
    	}
    },

    sortedProducts () {
    	const products = cloneDeep(this.products)

    	return products.sort((a, b) => {
    		const _a = a.prices.find(p => p.selected).price
    		const _b = b.prices.find(p => p.selected).price
    		return _a > _b ? 1 : -1
    	})
    }
	},

	methods: {
		addProduct (personId, product, doctor) {
			this.$loading.start()

			const productId = product.id
			const selectedOption = product.prices.find(p => p.selected)

			return ProductHelper.selectProductOption(personId, productId, selectedOption)
				.then(basket => ProductHelper.addProductDoctorToBasket(personId, productId, doctor, basket))
				.then(basket => this.updateBasket(basket))
        .then(basket => {
          this.$EventBus.$emit(EVENT_PRODUCT_ADDED, {
            basket,
            personId,
            categoryId: this.categoryId,
            productId,
            source: this.source,
          })
        	this.$emit('added')
          this.$loading.end()
          return basket
        })
        .catch(LogHelper.error)
		},

		simulate () {
			const products = this.getProductsFromGroup(this.personId, this.groupId)
			const triggerAmount = products.length
			let trigger = 0

			// use the same criteria selection for both products
			const selectedOption = this.product.prices.find(p => p.selected)

			products.forEach(product => {
				ProductHelper.addProductToBasket(this.personId, product.id, this.doctor)
					.then(basket => ProductHelper.selectProductOption(this.personId, product.id, selectedOption, basket))
					.then(basket => this.$backend.ovp.simulateUpdateBasket(basket))
					.then(basket => {
						this.products.push(Object.assign(
							{},
							{ id: product.id },
							basket.persons.find(p => p.personId === this.personId).products.products[product.id]
						))

						trigger++

						if (triggerAmount === trigger) {
							this.ready = true
						}
					})
			})
		}
	}
}
</script>

<style name="mobile" scoped>
.suggestion {
	margin: 15px;

	&-products {
		display: grid;
    gap: 14px;
    padding: 0 0 20px 0;
	}

	&-product {
		border: 1px solid var(--c-black);
		flex: 1 1 100%;
	}

	.message {
		margin: 24px 0;
	}
}
</style>

<style name="desktop" scoped>
@media (--md) {
	.suggestion {
		margin: 20px;

		h2 {
			font-weight: bold;
			font-size: 24px;
			line-height: 30px;
		}

		&-products {
			grid-template-columns: 1fr 1fr;
      gap: 24px;
		}

		.product {
		}
	}
}
</style>